import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { ShopifyProduct, fetchProducts } from "@/lib/shopify";
import { uniqueDisplayNames } from "@/lib/productTitle";
import { diversifyRail } from "@/lib/railDiversity";
import { getCategoryBySlug } from "@/lib/categories";
import { exactTypeClause } from "@/lib/productTypes";
import { plainProductTypes } from "../../../scripts/category-trail.mjs";
import { ProductCard } from "@/components/ProductCard";

interface ArticleProductBlockProps {
  /** Internal link from the article, e.g. "/category/area-rugs" */
  categoryPath: string;
  heading?: string;
}

const COUNT = 3;

/**
 * A category that lists several product types is named for the first (Rugs:
 * rugs, then bath rugs, rug pads, doormats), and Shopify's best-selling order
 * clusters by type: the first 24 of the whole Rugs query were rug pads and bath
 * mats, so a rail headed "Rugs" showed no rug. The rail asks for the lead type.
 * Not for All Products / Best Sellers / New Arrivals (useStaticFilters).
 */
function leadTypeQuery(category: { query?: string; useStaticFilters?: boolean }): string | null {
  if (category.useStaticFilters) return null;
  const types = plainProductTypes(category.query);
  return types && types.length > 1 ? exactTypeClause(types[0]) : null;
}

/**
 * Inline commercial moment inside a journal article. Sourced from the article's
 * own internalLinks, so it always matches the subject matter. Renders nothing at
 * all when the category yields no products — an article must never show an empty
 * shelf or a "no products" message.
 */
export const ArticleProductBlock = ({
  categoryPath,
  heading = "Shop the category",
}: ArticleProductBlockProps) => {
  const slug = categoryPath.replace(/^\/category\//, "").split(/[?#]/)[0];
  const category = getCategoryBySlug(slug);
  const [products, setProducts] = useState<ShopifyProduct[]>([]);

  useEffect(() => {
    if (!category) return;
    let cancelled = false;
    (async () => {
      try {
        // Over-fetch so diversity + dedupe still leave three to show.
        const options = { sortKey: category.sortKey || "BEST_SELLING", reverse: category.reverse };
        const pick = (pool: ShopifyProduct[]) => diversifyRail(pool, { count: COUNT, maxPerType: 2, seed: `journal-${slug}` });
        const lead = leadTypeQuery(category);
        let picked = lead ? pick(await fetchProducts(24, lead, options)) : [];
        // A thin lead type (Loft Beds: 4 in stock) falls back to the whole category.
        if (picked.length < COUNT) picked = pick(await fetchProducts(24, category.query, options));
        if (cancelled) return;
        setProducts(picked);
      } catch {
        if (!cancelled) setProducts([]);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const names = useMemo(() => uniqueDisplayNames(products), [products]);

  if (!category || products.length === 0) return null;

  return (
    <section className="not-prose my-12 border-t border-border/60 pt-8">
      <div className="flex items-baseline justify-between gap-3 mb-5">
        <h2 className="text-[11px] md:text-[12px] tracking-[0.22em] uppercase font-bold text-foreground">
          {heading}
        </h2>
        <Link
          to={`/category/${slug}`}
          className="text-[10px] tracking-[0.18em] uppercase font-bold text-foreground hover:text-primary inline-flex items-center gap-1.5 shrink-0"
        >
          {category.name} <ArrowRight className="h-3 w-3" />
        </Link>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-5">
        {products.map((p) => (
          <ProductCard key={p.node.id} product={p} displayName={names[p.node.handle]} openingPrice />
        ))}
      </div>
    </section>
  );
};
